import * as d3 from 'd3'
// import {responsivefy} from "@/common/functions";

/**
 * Helper function to map ETL status to a color
 */
function getStatusColor(status) {
    switch (status) {
        case 'success':
            return '#28a745'; // Green
        case 'failure':
            return '#dc3545'; // Red
        case 'error':
            return '#dc3545'; // Red
        case 'started':
        case 'running':
            return '#007bff'; // Blue
        case 'not_started':
        case 'pending':
        default:
            return '#6c757d'; // Gray
    }
}

export default function (chart, divId, vizName, resData, gridflag) {
    // --- 1. Define variables ---
    let svg, gLink, gNode;
    let idCounter = 0;
    const duration = 350;
    const nodeHeight = 20;
    const X_OFFSET = 20;
    // --- 2. Clear old SVG ---
    d3.select(divId).select('svg').remove();

    // --- 3. Setup SVG ---
    svg = d3
        .select(divId)
        .append('svg')
        .attr('width', chart.width + 2 * chart.margin.left + chart.margin.right)
        .attr('height', chart.height + 5 * chart.margin.top + chart.margin.bottom)
        // .call(responsivefy)
        .attr("transform", "translate(" + 20 + "," + 20 + ")");

    gLink = svg.append('g').attr('fill', 'none').attr('stroke', '#999').attr('stroke-width', 1.5);
    gNode = svg.append('g');

    // --- 4. Add Column Headers ---
    const columns = chart.vizSpecific7.columns || []
    console.log("columns",columns)

    for (const { label, x } of columns) {
        svg
            .append('text')
            .attr('dy', '0.32em')
            .attr('y', chart.margin.top - 20)
            .attr('x', x)
            .attr('text-anchor', 'start')
            .attr('font-weight', 'bold')
            .text(label);
    }

    console.log("res", chart, gridflag);

    // --- 5. Data Setup ---
    function buildHierarchy(data, chart) {

        if (!Array.isArray(data) || data.length === 0) {
            return {
                name: chart?.y1AxisDisplayName || "Root",
                metric1: 0,
                metric2:0,
                metric3:0,
                dim4_name: "",
                dim5_name:"",
                dim6_name:"",
                children: []
            };
        }

        function groupBy(data, dimensionKeys, level = 0) {
            const key = dimensionKeys[level];

            const map = new Map();
            data.forEach(row => {
                const groupKey = row[key] ?? "Unknown";   // handle null / undefined
                if (!map.has(groupKey)) map.set(groupKey, []);
                map.get(groupKey).push(row);
            });

            const result = [];

            map.forEach((rows, groupName) => {
                if (!rows || rows.length === 0) return; // skip empty groups


                const nodeMetric1 = rows.reduce((sum, r) => sum + (r.metric1 || 0), 0);
                const nodeMetric2 = (rows[0]?.metric2 ?? 0);
                const nodeMetric3 = (rows[0]?.metric2 ?? 0);
                const nodeDim4 = rows[0]?.dim4_name ?? "";
                const nodeDim5 = rows[0]?.dim5_name ?? "";
                const nodeDim6 = rows[0]?.dim6_name ?? "";
                const nodeDim7 = rows[0]?.dim7_name ?? "";
                const nodeDim8 = rows[0]?.dim8_name ?? "";
                const nodeDim9 = rows[0]?.dim9_name ?? "";
                const nodeDim10 = rows[0]?.dim10_name ?? "";

                // STOP at dim3_name → leaf node
                if (level === dimensionKeys.length - 1) {
                    result.push({
                        name: groupName || "Unknown",
                        metric1: nodeMetric1,
                        metric2: nodeMetric2,
                        metric3: nodeMetric3,
                        dim4_name: nodeDim4,
                        dim5_name: nodeDim5,
                        dim6_name: nodeDim6,
                        dim7_name: nodeDim7,
                        dim8_name: nodeDim8,
                        dim9_name: nodeDim9,
                        dim10_name: nodeDim10,
                        children: [] // force no children here
                    });
                } else {
                    result.push({
                        name: groupName || "Unknown",
                        metric1: nodeMetric1,
                        metric2: nodeMetric2,
                        metric3: nodeMetric3,
                        dim4_name: nodeDim4,
                        dim5_name: nodeDim5,
                        dim6_name: nodeDim6,
                        dim7_name: nodeDim7,
                        dim8_name: nodeDim8,
                        dim9_name: nodeDim9,
                        dim10_name: nodeDim10,
                        children: groupBy(rows, dimensionKeys, level + 1)
                    });
                }
            });

            return result;
        }

        const dimensionOrder = chart.vizSpecific7.dimensionOrder || [];
        console.log("dimensionOrder",dimensionOrder);

        const children = groupBy(data, dimensionOrder);

        return {
            name: chart?.y1AxisDisplayName || "Root",
            metric1: data.reduce((s, r) => s + (r.metric1 || 0), 0),
            metric2: "-",
            metric3: "-",
            dim4_name: "-",
            dim5_name: "-",
            dim6_name: "-",
            dim7_name: "-",
            dim8_name: "-",
            dim9_name: "-",
            dim10_name: "-",
            children
        };
    }




    let treeData = buildHierarchy(resData, chart);
    console.log("Tree Data:", treeData);


    var root = d3.hierarchy(treeData, function (d) {
        return d.children;
    })

    // Initialize positions
    root.x0 = 0;
    root.y0 = chart.margin.top;

    // Collapse all children initially (keep them in _children)
    root.descendants().forEach((d) => {
        d._children = d.children;
        if (d.depth > 0) {
            d.children = null;
        }
    });

    // --- 6. Define Core Update/Click Functions ---

    /**
     * Calculate tree layout - simple sequential positioning
     */
    function calculateLayout(source) {
        console.log(source)
        let yPosition = chart.margin.top;

        function traverse(node, depth) {
            // Set position
            node.x = depth * 30 + X_OFFSET; // Horizontal indentation
            node.y = yPosition + nodeHeight / 2; // Center the node

            // Store absolute Y position for column alignment
            node.absoluteY = yPosition + nodeHeight / 2;

            yPosition += nodeHeight; // Move down for next node

            // Process children if they are visible
            if (node.children) {
                node.children.forEach(child => traverse(child, depth + 1));
            }
        }

        yPosition = chart.margin.top;
        traverse(root, 0);

        return yPosition; // Return total height used
    }

    /**
     * Proper hierarchical link path
     */
    function getLinkPath(d) {
        const sourceX = d.source.x;
        const sourceY = d.source.y;
        const targetX = d.target.x;
        const targetY = d.target.y;

        // Create elbow connector: vertical then horizontal
        return `
      M${sourceX},${sourceY}
      V${targetY}
      H${targetX}
    `;
    }

    /**
     * Toggles children on click
     */
    function click(event, datum) {
        const node = (datum && typeof datum === 'object') ? datum : d3.select(this).datum();
        if (!node) return;

        if (node.children) {
            node._children = node.children;
            node.children = null;
        } else {
            node.children = node._children;
            node._children = null;
        }

        update(node);
    }

    /**
     * The main D3 update function
     */
    function update(source) {
        const totalHeight = calculateLayout(source);

        // Update SVG height
        svg
            .attr('height', Math.max(600, totalHeight + chart.margin.bottom));

        const nodes = root.descendants();
        const links = root.links();

        // --- 1. Update Nodes ---
        const node = gNode
            .selectAll('g.node')
            .data(nodes, (d) => d.id || (d.id = ++idCounter));

        // **ENTER (Create new nodes)**
        const nodeEnter = node
            .enter()
            .append('g')
            .classed('node', true)
            .attr('transform', `translate(${source.x},${source.y})`)
            .style('opacity', 0)
            .style('cursor', 'pointer')
            .on('click', click);

        // Add Circle
        nodeEnter
            .append('circle')
            .attr('r', 6)
            .attr('fill', (d) => getStatusColor(d.data.dim4_name));

        // Add Text Label
        nodeEnter
            .append('text')
            .classed('label', true)
            .attr('dy', '0.32em')
            .attr('x', 12)
            .attr('fill', (d) => getStatusColor(d.data.dim4_name))
            .attr('font-weight', (d) => (d._children || d.children) ? 'bold' : 'normal')
            .text((d) => d.data.name);

        // **UPDATE (Transition existing nodes)**
        const nodeUpdate = node
            .merge(nodeEnter)
            .transition()
            .duration(duration)
            .attr('transform', (d) => `translate(${d.x},${d.y})`)
            .style('opacity', 1);

        // Update circle
        nodeUpdate
            .select('circle')
            .attr('fill', (d) => getStatusColor(d.data.dim4_name))
            .attr('r', (d) => (d._children || d.children) ? 6 : 4);

        // Update main label
        nodeUpdate
            .select('.label')
            .attr('fill', (d) => getStatusColor(d.data.dim4_name))
            .attr('font-weight', (d) => (d._children || d.children) ? 'bold' : 'normal')
            .text((d) => d.data.name);

        // **EXIT (Remove old nodes)**
        node
            .exit()
            .transition()
            .duration(duration)
            .attr('transform', `translate(${source.x},${source.y})`)
            .style('opacity', 0)
            .remove();

        // --- 2. Update Column Texts (Separate from nodes for proper alignment) ---

        // --- 2. Update Column Texts (Dynamic based on columns array) ---

// Remove previous column text elements
        svg.selectAll('.column-text').remove();

// Loop through each column definition
        columns.forEach((col) => {

            svg.selectAll(`.column-text-${col.key}`)
                .data(nodes)
                .enter()
                .append('text')
                .attr('class', `column-text column-text-${col.key}`)
                .attr('dy', '0.32em')
                .attr('x', col.x)
                .attr('y', (d) => d.absoluteY)
                .attr('text-anchor', 'start')
                .attr('fill', (d) =>
                    col.key === "dim4_name" ? getStatusColor(d.data[col.key]) : "#555"
                )
                .text((d) => {
                    const v = d.data[col.key];
                    return v != null ? v : '--';
                });
        });


        // --- 3. Update Links ---
        const link = gLink
            .selectAll('path')
            .data(links, (d) => d.target.id);

        // **ENTER (Create new links)**
        const linkEnter = link
            .enter()
            .insert('path', 'g')
            .attr('d', () => {
                const o = {
                    source: { x: source.x, y: source.y },
                    target: { x: source.x, y: source.y },
                };
                return getLinkPath(o);
            })
            .attr('stroke-opacity', 0);

        // **UPDATE (Transition existing links)**
        link
            .merge(linkEnter)
            .transition()
            .duration(duration)
            .attr('d', getLinkPath)
            .attr('stroke-opacity', 1);

        // **EXIT (Remove old links)**
        link
            .exit()
            .transition()
            .duration(duration)
            .attr('d', () => {
                const o = {
                    source: { x: source.x, y: source.y },
                    target: { x: source.x, y: source.y },
                };
                return getLinkPath(o);
            })
            .attr('stroke-opacity', 0)
            .remove();
    }

    // --- 7. Initial Draw ---
    update(root);
}